import React, {ReactElement, useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import Select from "react-select";
import {getReceivers} from "./api";
import {FilterComponentProps, ReceiverOption} from "./types";

function FilterComponent({children, title, className}: FilterComponentProps): ReactElement {
    return (
        <div className={'filter' + (className ? ' ' + className : '')}>
            <label className="filter-title">{title}</label>
            <div className="filter-body">{children}</div>
        </div>
    );
}

export function DataPeriod(): ReactElement {
    const form = useFormContext();
    const {t} = useTranslation();
    const dateRanges = [
        {value: '0', label: t('user-page.wzs.filters.date-period-options.last-7-days')},
        {value: '1', label: t('user-page.wzs.filters.date-period-options.last-14-days')},
        {value: '2', label: t('user-page.wzs.filters.date-period-options.last-30-days')},
        {value: '3', label: t('user-page.wzs.filters.date-period-options.last-60-days')},
        {value: '4', label: t('user-page.wzs.filters.date-period-options.date-range')},
    ];

    return (
        <FilterComponent title={t('user-page.wzs.filters.choice-period')} className="date-period">
            <select defaultValue="0" {...form.register('date-period')}>
                {dateRanges.map((range) => <option key={range.value} value={range.value}>{range.label}</option>)}
            </select>
        </FilterComponent>
    );
}

export function DateRange(): ReactElement {
    const form = useFormContext();
    const {t} = useTranslation();

    return (
        <FilterComponent title={t('user-page.wzs.filters.date-range')} className="date-range">
            <input
                type="date"
                required
                aria-label="date-from"
                {...form.register('date-from', {required: true})}
            />
            <input
                type="date"
                required
                aria-label="date-to"
                {...form.register('date-to', {required: true})}
            />
        </FilterComponent>
    );
}

export function DocumentType(): ReactElement {
    const form = useFormContext();
    const {t} = useTranslation();
    const documentTypes = [
        {value: 'all', label: t('user-page.wzs.filters.document-types.all')},
        {value: 'sale', label: t('user-page.wzs.filters.document-types.sale')},
        {value: 'return', label: t('user-page.wzs.filters.document-types.return')},
    ];

    return (
        <FilterComponent title={t('user-page.wzs.filters.document-type')} className="document-type">
            <select defaultValue="all" {...form.register('document-type')}>
                {documentTypes.map((documentType) => (
                    <option key={documentType.value} value={documentType.value}>{documentType.label}</option>
                ))}
            </select>
        </FilterComponent>
    );
}

export function ReceiverFilter(): ReactElement {
    const form = useFormContext();
    const {t} = useTranslation();
    const [receivers, setReceivers] = useState<ReceiverOption[]>([]);

    useEffect(() => {
        getReceivers()
            .then((receivers) => {
                const options = receivers.map((receiver) => ({
                    value: receiver.id,
                    label: receiver.name + ' - ' + receiver.address.street + ', ' + receiver.address.postalCode + ' ' + receiver.address.city,
                }));

                setReceivers(options.length > 1
                    ? [{value: '', label: t('user-page.wzs.filters.all-receivers')}, ...options]
                    : []);
            });
    }, [t]);

    if (receivers.length === 0) {
        return <></>;
    }

    return (
        <FilterComponent title={t('user-page.wzs.filters.receiver')} className="receiver">
            <Select
                onChange={(receiver: ReceiverOption | null) => form.setValue('receiver', receiver?.value ?? '')}
                placeholder={t('user-page.wzs.filters.all-receivers')}
                options={receivers}
            />
        </FilterComponent>
    );
}

export function WZNumberSearch(): ReactElement {
    const form = useFormContext();
    const {t} = useTranslation();

    return (
        <FilterComponent title={t('user-page.wzs.filters.search-wz-number')} className="wz-number">
            <input type="search" {...form.register('wz-number')} />
        </FilterComponent>
    );
}

export function ProductEAN(): ReactElement {
    const form = useFormContext();
    const {t} = useTranslation();

    return (
        <FilterComponent title={t('user-page.wzs.filters.product-ean')} className="product-ean">
            <input type="text" {...form.register('product-ean')} />
        </FilterComponent>
    );
}

export function ProductIndex(): ReactElement {
    const form = useFormContext();
    const {t} = useTranslation();

    return (
        <FilterComponent title={t('user-page.wzs.filters.product-index')} className="product-index">
            <input type="text" {...form.register('product-index')} />
        </FilterComponent>
    );
}
